import { useState } from 'react';
import { Link } from 'react-router-dom';

export interface AuditFinding {
  id: string;
  audit_id: string;
  auditor: 'cto' | 'ciso';
  repo: string;
  domain: string;
  severity: 'info' | 'warn' | 'high' | 'critical';
  title: string;
  detail: string;
  file: string | null;
  remediation: string | null;
  standard: string | null;
  status: 'open' | 'acknowledged' | 'resolved';
  first_seen_at: string;
  last_seen_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

interface Props {
  findings: AuditFinding[];
  labels?: Record<string, string>;
  onAck: (id: string) => void;
  onResolve: (id: string) => void;
  busyId: string | null;
}

function severityBadgeClass(severity: string): string {
  if (severity === 'critical' || severity === 'high') return 'badge-red';
  if (severity === 'warn') return 'badge-yellow';
  return 'badge-gray';
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isAging(firstSeenIso: string): boolean {
  const days = (Date.now() - new Date(firstSeenIso).getTime()) / (1000 * 60 * 60 * 24);
  return days > 14;
}

interface DraftFixState {
  status: 'busy' | 'error' | 'done';
  trackId?: string;
  error?: string;
}

export default function FindingsList({ findings, labels, onAck, onResolve, busyId }: Props) {
  const [draftFix, setDraftFix] = useState<Record<string, DraftFixState>>({});

  async function handleDraftFix(id: string) {
    setDraftFix((s) => ({ ...s, [id]: { status: 'busy' } }));
    try {
      const res = await fetch(`/api/audit-findings/${id}/draft-fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDraftFix((s) => ({ ...s, [id]: { status: 'error', error: body?.error ?? `request failed: ${res.status}` } }));
        return;
      }
      setDraftFix((s) => ({ ...s, [id]: { status: 'done', trackId: body.track_id } }));
    } catch (err) {
      setDraftFix((s) => ({ ...s, [id]: { status: 'error', error: err instanceof Error ? err.message : String(err) } }));
    }
  }

  if (findings.length === 0) {
    return <div className="empty">No open findings in this scope.</div>;
  }

  return (
    <div>
      {findings.map((f) => {
        const busy = busyId === f.id;
        const draft = draftFix[f.id];
        return (
          <details key={f.id} className="card" style={{ display: 'block' }}>
            <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
              <div className="badge-row" style={{ flexWrap: 'wrap' }}>
                <span className={`badge ${severityBadgeClass(f.severity)}`}>{f.severity}</span>
                <span className="badge badge-gray">{labels?.[f.domain] ?? f.domain}</span>
                {isAging(f.first_seen_at) && <span className="badge badge-yellow">aging</span>}
                <span className="card-sub">{f.title}</span>
              </div>
              {f.file && <div className="card-sub code">{f.file}</div>}
            </summary>
            <div style={{ marginTop: 10 }}>
              <div className="one-line">{f.detail}</div>
              {f.remediation && (
                <div className="kv" style={{ display: 'block' }}>
                  <strong>remediation:</strong> <span>{f.remediation}</span>
                </div>
              )}
              {f.standard && (
                <div className="kv">
                  <strong>standard:</strong> <span>{f.standard}</span>
                </div>
              )}
              <div className="kv">
                <strong>first seen:</strong> <span>{relativeTime(f.first_seen_at)}</span>
                <strong>last seen:</strong> <span>{relativeTime(f.last_seen_at)}</span>
              </div>
              <div className="actions">
                <button
                  className="btn-outline-conductor"
                  disabled={busy || f.status !== 'open'}
                  onClick={(e) => { e.preventDefault(); onAck(f.id); }}
                >
                  Acknowledge
                </button>
                <button
                  className="btn-conductor"
                  disabled={busy || f.status === 'resolved'}
                  onClick={(e) => { e.preventDefault(); onResolve(f.id); }}
                >
                  Mark resolved
                </button>
                {f.status === 'open' && (
                  draft?.status === 'done' ? (
                    <Link className="btn-outline-conductor" to={`/dashboard?track=${draft.trackId}`}>
                      Review draft →
                    </Link>
                  ) : (
                    <button
                      className="btn-outline-conductor"
                      disabled={draft?.status === 'busy'}
                      onClick={(e) => { e.preventDefault(); handleDraftFix(f.id); }}
                    >
                      Draft fix
                    </button>
                  )
                )}
              </div>
              {draft?.status === 'error' && <div className="errors">{draft.error}</div>}
            </div>
          </details>
        );
      })}
    </div>
  );
}
